/**
 * Render-time normalization for recipe instructions.
 *
 * Scraped recipes (notably Ricardo Cuisine) persist fragmented instruction data:
 *  - Section titles stored as their own numbered steps ("Porc braisé", "Tacos").
 *  - Single sentences split across entries at <br>-derived newlines
 *    ("Placer la grille au centre du four. Préchauffer" + "le four à 180 °C.").
 *
 * This util repairs that at display time so every recipe reads correctly
 * regardless of source. It:
 *  1. strips the publisher's step numbers ("1. ", "2 ", "1Dans" — see
 *     stepNumbering.ts) + collapses internal whitespace,
 *  2. merges continuation fragments (a step starting lowercase continues the
 *     previous one — sentences start uppercase in FR/EN),
 *  3. classifies short, un-punctuated, non-final entries as section headers,
 *     and short labels ending in a colon ("Pour le BBQ:") too,
 *  4. groups the real steps under those headers into sections.
 */

import { stripStepNumbers, isGenericStepsLabel } from './stepNumbering';

export interface RawInstruction {
  number?: number;
  step: string;
}

export interface NormalizedStep {
  number: number;
  step: string;
}

export interface NormalizedSection {
  /** undefined for a leading/unnamed section */
  name?: string;
  steps: NormalizedStep[];
}

export interface NormalizedInstructions {
  sections: NormalizedSection[];
  /** Flat, continuously-numbered list of the real steps (headers excluded). */
  steps: NormalizedStep[];
  /** True when at least one named section header was detected. */
  hasSections: boolean;
}

const TERMINAL_PUNCT_RE = /[.!?:;]["'”’)]?$/;
const HEADER_MAX_WORDS = 8;

/** Collapse whitespace/newlines. Numbering is stripped list-wide, before this. */
function clean(text: string): string {
  return (text || '').replace(/\s+/g, ' ').trim();
}

/**
 * Strip numbering across the whole list (a bare "2 " is only a step number when
 * the list counts up — stepNumbering.ts), then clean each entry. `numbered`
 * stays aligned with the returned texts.
 */
function cleanAll(rawSteps: string[]): { texts: string[]; numbered: boolean[] } {
  const stripped = stripStepNumbers(rawSteps);
  return { texts: stripped.texts.map(clean), numbered: stripped.numbered };
}

const COLON_LABEL_RE = /:["'”’)]?$/;

/** A step whose first alphabetic character is lowercase continues the previous one. */
function isContinuation(text: string): boolean {
  const firstLetter = text.match(/\p{L}/u);
  if (!firstLetter) return false; // starts with a digit/punct — treat as its own step
  return firstLetter[0] === firstLetter[0].toLowerCase() &&
    firstLetter[0] !== firstLetter[0].toUpperCase();
}

function endsWithTerminalPunct(text: string): boolean {
  return TERMINAL_PUNCT_RE.test(text);
}

/**
 * Could this entry be a section label? Short and unpunctuated ("Marinade"),
 * unless the publisher numbered it as a step; or short and ending in a colon
 * ("Pour le BBQ:", "Cuisson au four:"), which labels what follows even when the
 * page numbered it along with the steps.
 */
function isHeaderCandidate(text: string, numbered: boolean): boolean {
  if (text.split(' ').length > HEADER_MAX_WORDS) return false;
  if (COLON_LABEL_RE.test(text)) return true;
  return !numbered && !endsWithTerminalPunct(text);
}

/** A label's display name: the colon was punctuation, not part of the name. */
function headerName(text: string): string {
  return text.replace(/\s*:\s*$/, '');
}

/**
 * Header flags for a RAW instruction list, one per original entry.
 *
 * Merging comes first and is not optional. Plenty of scraped recipes store a
 * single sentence split across a dozen entries; judged individually those
 * fragments are all short and unpunctuated, so a classifier run on the raw list
 * calls almost every one of them a header. Merge, classify, then hand the
 * verdict back down to each fragment of the merged step.
 *
 * Exported because step chips keep the raw list and must reach the same verdict
 * this normalizer does: a header is a label, not something a cook does, so
 * nothing should be attached to it.
 */
export function classifyRawSectionHeaders(rawSteps: string[]): boolean[] {
  const { texts: cleaned, numbered } = cleanAll(rawSteps);
  const merged: string[] = [];
  const mergedNumbered: boolean[] = [];
  // Which merged entry each original index landed in (-1 for dropped blanks).
  const groupOf = cleaned.map((text, i) => {
    if (!text) return -1;
    if (merged.length > 0 && isContinuation(text)) {
      merged[merged.length - 1] = `${merged[merged.length - 1]} ${text}`;
      return merged.length - 1;
    }
    merged.push(text);
    mergedNumbered.push(numbered[i]);
    return merged.length - 1;
  });
  const headers = classifySectionHeaders(merged, mergedNumbered);
  return groupOf.map(g => (g < 0 ? false : headers[g]));
}

/**
 * Which entries of an ALREADY-MERGED instruction list are section headers
 * rather than real steps: short, no terminal punctuation, and followed by at
 * least one real step ("Marinade", "Pour la glace", "Crémeux au chocolat").
 * A short label ending in a colon counts too ("Pour le BBQ:"). `numbered` marks
 * entries the publisher numbered as steps (stepNumbering.ts): those are steps
 * unless they end in a colon.
 */
export function classifySectionHeaders(steps: string[], numbered: boolean[] = []): boolean[] {
  const candidate = steps.map((text, i) => isHeaderCandidate(text, !!numbered[i]));
  const headers = steps.map((_, i) => {
    if (!candidate[i]) return false;
    // must be followed by something that is itself a real step (not a header)
    for (let j = i + 1; j < steps.length; j++) {
      if (!candidate[j]) return true;
    }
    return false;
  });
  // A section opens on a step, not on another label: "Cuisson au four:" then
  // "Préchauffer le four à 400˚F" is a label and its first step (#794), which
  // only the publisher's numbers used to tell apart. A colon still labels, and
  // a leading « Préparation » titles the list rather than opening a section;
  // a list title is never demoted into a step (« Étapes » is not something to do).
  for (let i = 1; i < steps.length; i++) {
    const afterLabel = headers[i - 1] && !(i - 1 === 0 && isGenericStepsLabel(steps[0]));
    if (headers[i] && afterLabel && !COLON_LABEL_RE.test(steps[i]) && !isGenericStepsLabel(steps[i])) {
      headers[i] = false;
    }
  }
  return headers;
}

/**
 * Coerce a persisted `instructionSections` value into the shape the app reads.
 *
 * Storage is not consistent: the scraper DTO carries `steps: string[]` and two
 * writers persisted it verbatim, so ~28 recipes hold plain strings where the
 * read path expects `{ number, step }`. Reading `.step` off a string yields
 * `undefined`, every step collapses to '', and the section renders empty —
 * silently, because Firestore hands the value over as `any`.
 *
 * Accepts either shape (and a mix of the two within one section) and drops
 * entries with no text. Numbering stays PER SECTION and an existing `number`
 * is preserved, which is what the writers and the detail screen already do —
 * the 16 recipes that render correctly today must not renumber.
 *
 * Returns `[]` when nothing renderable survives — a few recipes carry a named
 * section with no steps at all. Callers MUST treat an empty result as "no
 * usable sections" and fall back to the flat `instructions[]` list, or those
 * recipes render a heading and nothing else.
 */
export function coerceInstructionSections(raw: unknown): NormalizedSection[] {
  if (!Array.isArray(raw)) return [];

  const sections: NormalizedSection[] = [];

  for (const section of raw) {
    if (!section || typeof section !== 'object') continue;
    const rawSteps = (section as { steps?: unknown }).steps;
    if (!Array.isArray(rawSteps)) continue;

    const steps: NormalizedStep[] = [];
    for (const entry of rawSteps) {
      const isObject = !!entry && typeof entry === 'object';
      const text = typeof entry === 'string'
        ? entry
        : (isObject ? (entry as { step?: unknown }).step : undefined);
      const cleaned = typeof text === 'string' ? text.trim() : '';
      if (!cleaned) continue;
      const stored = isObject ? (entry as { number?: unknown }).number : undefined;
      const number = typeof stored === 'number' && Number.isFinite(stored) && stored > 0
        ? stored
        : steps.length + 1;
      steps.push({ number, step: cleaned });
    }

    if (steps.length === 0) continue;
    const name = (section as { name?: unknown }).name;
    sections.push({
      ...(typeof name === 'string' && name.trim() ? { name: name.trim() } : {}),
      steps,
    });
  }

  return sections;
}

/**
 * Normalize a flat instruction array into merged steps + optional sections.
 */
export function normalizeInstructions(raw: RawInstruction[] | undefined | null): NormalizedInstructions {
  const { texts, numbered } = cleanAll((raw || []).map(r => r?.step ?? ''));

  // 1. Merge continuation fragments into the previous entry.
  const merged: string[] = [];
  const mergedNumbered: boolean[] = [];
  texts.forEach((text, i) => {
    if (!text) return;
    if (merged.length > 0 && isContinuation(text)) {
      merged[merged.length - 1] = `${merged[merged.length - 1]} ${text}`;
    } else {
      merged.push(text);
      mergedNumbered.push(numbered[i]);
    }
  });

  // 2. Classify each merged entry as a header or a real step.
  const isHeader = classifySectionHeaders(merged, mergedNumbered);

  // 3. Build sections + a continuously-numbered flat step list.
  const sections: NormalizedSection[] = [];
  const flat: NormalizedStep[] = [];
  let current: NormalizedSection = { steps: [] };
  let counter = 0;

  const pushCurrent = () => {
    if (current.steps.length > 0 || current.name) sections.push(current);
  };

  merged.forEach((text, i) => {
    // « Étapes à suivre » opening the list titles the list, which the screen
    // already does; it is not a section of the recipe.
    if (i === 0 && isHeader[i] && isGenericStepsLabel(text)) return;
    if (isHeader[i]) {
      // Close the previous section, open a new named one.
      pushCurrent();
      current = { name: headerName(text), steps: [] };
    } else {
      counter += 1;
      const step: NormalizedStep = { number: counter, step: text };
      current.steps.push(step);
      flat.push(step);
    }
  });
  pushCurrent();

  const hasSections = sections.some(s => !!s.name);

  return { sections, steps: flat, hasSections };
}

export default normalizeInstructions;
